import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Locale-aware formatting. Money and quantities arrive as decimal strings
// (Postgres numeric) and are formatted without converting through float math.

export const localeFor = (lang: string) => (lang === "pl" ? "pl-PL" : "en-GB");

export function useFormat() {
  const { i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  return useMemo(() => {
    const money = new Map<string, Intl.NumberFormat>();
    const qty = new Intl.NumberFormat(locale, { maximumFractionDigits: 3 });
    const day = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    });
    const dayYear = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const longDay = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    const dateTime = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
    const shortDate = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    });
    const qtyByDecimals = new Map<number, Intl.NumberFormat>();
    const count = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const pct = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
    const time = new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });
    const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" });
    return {
      money(amount: string, currency: string): string {
        let f = money.get(currency);
        if (!f) {
          f = new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
          });
          money.set(currency, f);
        }
        return f.format(amount as Intl.StringNumericLiteral);
      },
      qty: (value: string) => qty.format(value as Intl.StringNumericLiteral),
      /** Quantity with the unit's precision (uoms.decimals), never rounded further. */
      qtyIn(value: string | number, decimals: number): string {
        let f = qtyByDecimals.get(decimals);
        if (!f) {
          f = new Intl.NumberFormat(locale, {
            maximumFractionDigits: Math.max(decimals, 0),
          });
          qtyByDecimals.set(decimals, f);
        }
        return f.format(String(value) as Intl.StringNumericLiteral);
      },
      dateTime: (iso: string) => dateTime.format(new Date(iso)),
      /** Day and month of a timestamp ("22 Oct"), for compact timelines. */
      shortDate: (iso: string) => shortDate.format(new Date(iso)),
      day: (iso: string) => day.format(new Date(`${iso}T12:00:00`)),
      dayYear: (iso: string) => dayYear.format(new Date(`${iso}T12:00:00`)),
      longDay: (d: Date) => longDay.format(d),
      /** Whole number with grouping ("1 234"). */
      count: (n: number) => count.format(n),
      /** Percent value with at most one decimal, without the sign ("94,5"). */
      pct: (n: number) => pct.format(n),
      time: (iso: string) => time.format(new Date(iso)),
      /** Short weekday name, 0 = Monday. */
      weekday: (i: number) => weekday.format(new Date(2024, 0, 1 + i)),
    };
  }, [locale]);
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
